import { useEffect } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  SelectItem,
} from "@heroui/react";
import {
  type CustomerPackaging,
  useCreatePackaging,
  useUpdatePackaging,
} from "@/api/use-packaging-api";
import { useToast } from "@/hooks/use-toast";
import {
  HeroFormField,
  HeroInputField,
  HeroNumberField,
  HeroSelectField,
} from "@/components/hero-form";
import { Switch } from "@heroui/react";
import { useTranslation } from "react-i18next";

type PackagingFormValues = {
  name: string;
  length: number;
  width: number;
  height: number;
  dimensionUnit: "cm" | "in";
  isDefault: boolean;
};

interface PackagingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packaging?: CustomerPackaging | null;
}

export function PackagingDialog({
  open,
  onOpenChange,
  packaging,
}: PackagingDialogProps) {
  const { t } = useTranslation("app");
  const { toast } = useToast();
  const createMutation = useCreatePackaging();
  const updateMutation = useUpdatePackaging();
  const isEditing = !!packaging;

  const packagingSchema = z.object({
    name: z.string().min(1, t("validation.nameRequired")).max(50),
    length: z.number().positive(t("validation.lengthPositive")),
    width: z.number().positive(t("validation.widthPositive")),
    height: z.number().positive(t("validation.heightPositive")),
    dimensionUnit: z.enum(["cm", "in"]),
    isDefault: z.boolean(),
  });

  const form = useForm<PackagingFormValues>({
    resolver: zodResolver(packagingSchema),
    defaultValues: {
      name: "",
      length: 0,
      width: 0,
      height: 0,
      dimensionUnit: "cm",
      isDefault: false,
    },
  });

  useEffect(() => {
    if (open) {
      if (packaging) {
        form.reset({
          name: packaging.name,
          length: packaging.length,
          width: packaging.width,
          height: packaging.height,
          dimensionUnit: packaging.dimensionUnit,
          isDefault: packaging.isDefault,
        });
      } else {
        form.reset({
          name: "",
          length: 0,
          width: 0,
          height: 0,
          dimensionUnit: "cm",
          isDefault: false,
        });
      }
    }
  }, [open, packaging, form]);

  const onSubmit = async (values: PackagingFormValues) => {
    try {
      if (isEditing && packaging) {
        await updateMutation.mutateAsync({ id: packaging.id, ...values });
        toast({ title: t("packagingDialog.updatedToast") });
      } else {
        await createMutation.mutateAsync(values);
        toast({ title: t("packagingDialog.savedToast") });
      }
      onOpenChange(false);
    } catch (error: unknown) {
      const message = axios.isAxiosError<{ error?: string }>(error)
        ? error.response?.data?.error
        : undefined;
      toast({
        variant: "destructive",
        title: isEditing
          ? t("packagingDialog.updateError")
          : t("packagingDialog.saveError"),
        description: message ?? t("packagingDialog.tryAgain"),
      });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="blur"
      disableAnimation
      size="md"
    >
      <ModalContent>
        {(onClose) => (
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <ModalHeader>
              {isEditing
                ? t("packagingDialog.editTitle")
                : t("packagingDialog.addTitle")}
            </ModalHeader>
            <ModalBody className="gap-4">
              <HeroInputField
                control={form.control}
                name="name"
                label={t("packagingDialog.name")}
                placeholder={t("packagingDialog.namePlaceholder")}
                variant="flat"
              />

              <div className="grid grid-cols-3 gap-3">
                <HeroNumberField
                  control={form.control}
                  name="length"
                  label={t("packagingDialog.length")}
                  step={0.1}
                  variant="flat"
                />
                <HeroNumberField
                  control={form.control}
                  name="width"
                  label={t("packagingDialog.width")}
                  step={0.1}
                  variant="flat"
                />
                <HeroNumberField
                  control={form.control}
                  name="height"
                  label={t("packagingDialog.height")}
                  step={0.1}
                  variant="flat"
                />
              </div>

              <HeroSelectField
                control={form.control}
                name="dimensionUnit"
                label={t("packagingDialog.unit")}
                variant="flat"
              >
                <SelectItem key="cm">
                  {t("packagingDialog.centimeters")}
                </SelectItem>
                <SelectItem key="in">{t("packagingDialog.inches")}</SelectItem>
              </HeroSelectField>

              <HeroFormField
                control={form.control}
                name="isDefault"
                render={({ field }) => (
                  <div className="flex items-center justify-between rounded-lg border border-default-200 p-3">
                    <div className="space-y-0.5">
                      <div className="text-medium font-medium">
                        {t("packagingDialog.setDefault")}
                      </div>
                      <p className="text-small text-default-500">
                        {t("packagingDialog.defaultDescription")}
                      </p>
                    </div>
                    <Switch
                      isSelected={field.value}
                      onValueChange={field.onChange}
                    />
                  </div>
                )}
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="light" type="button" onPress={onClose}>
                {t("actions.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                isDisabled={isLoading}
                isLoading={isLoading}
              >
                {isEditing ? t("actions.update") : t("actions.save")}
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
}
